import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Super-admin-only permanent delete for a single order.
// Guarded by exact PO number confirmation (typo-proof) and no side notifications.
export default function DeletePermanentlyDialog({ order, open, onOpenChange, onDeleted }) {
  const qc = useQueryClient();
  const [confirmPo, setConfirmPo] = useState("");
  const [busy, setBusy] = useState(false);
  const expected = (order?.po_number || "").trim();
  const canDelete = confirmPo.trim() === expected && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/orders/${order.id}`, { params: { confirm_po: expected } });
      toast.success(`Deleted permanently · ${expected}`);
      qc.invalidateQueries();
      onOpenChange(false);
      setConfirmPo("");
      if (typeof onDeleted === "function") { try { onDeleted(); } catch (_) {} }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setConfirmPo(""); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="delete-permanent-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-800">🗑 Delete Order Permanently</DialogTitle>
          <DialogDescription>
            This will permanently remove <b className="font-mono">{expected}</b> from the system.
            The customer record is kept — only this order and its billing artefacts
            are deleted. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">
            Type <b className="font-mono text-red-700">{expected}</b> to confirm
          </Label>
          <Input
            data-testid="delete-permanent-input"
            value={confirmPo}
            onChange={(e) => setConfirmPo(e.target.value)}
            placeholder={expected}
            className={`font-mono ${confirmPo && confirmPo !== expected ? "border-red-500" : ""}`}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="delete-permanent-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canDelete}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid="delete-permanent-submit"
          >
            {busy ? "Deleting…" : "🗑 Permanently Delete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
